import { CircleNotch, CheckCircle, Leaf, Recycle, Users, SealCheck } from '@phosphor-icons/react';

export const TransparencyCard = ({ score, breakdown, compact = false }) => {
  if (!score && score !== 0) return null;

  const getScoreColor = (value) => {
    if (value >= 80) return 'text-[#4A6C4C]';
    if (value >= 60) return 'text-[#D4A373]';
    return 'text-[#C26D53]';
  };

  const getScoreLabel = (value) => {
    if (value >= 80) return 'Excellent';
    if (value >= 60) return 'Good';
    return 'Fair';
  };

  if (compact) {
    return (
      <div className="flex items-center gap-2" data-testid="transparency-score-compact">
        <div className="relative w-10 h-10">
          <svg className="w-10 h-10 -rotate-90">
            <circle
              cx="20"
              cy="20"
              r="16"
              fill="none"
              stroke="hsl(40 18% 89%)"
              strokeWidth="4"
            />
            <circle
              cx="20"
              cy="20"
              r="16"
              fill="none"
              stroke="hsl(128 20% 36%)"
              strokeWidth="4"
              strokeDasharray={`${(score / 100) * 100.5} 100.5`}
              strokeLinecap="round"
            />
          </svg>
          <span className={`absolute inset-0 flex items-center justify-center text-xs font-semibold ${getScoreColor(score)}`}>
            {Math.round(score)}
          </span>
        </div>
        <span className="text-xs text-muted-foreground">Impact Score</span>
      </div>
    );
  }

  return (
    <div className="transparency-card bg-secondary/50 backdrop-blur-sm border border-border/60 p-6 rounded-xl" data-testid="transparency-card">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-1">Transparency Score</h3>
          <p className="text-sm text-muted-foreground">Verified impact metrics</p>
        </div>
        <SealCheck weight="duotone" className="w-8 h-8 text-[#4A6C4C]" />
      </div>

      {/* Main Score Ring */}
      <div className="flex items-center justify-center mb-8">
        <div className="relative w-32 h-32">
          <svg className="w-32 h-32 -rotate-90">
            <circle
              cx="64"
              cy="64"
              r="56"
              fill="none"
              stroke="hsl(40 18% 89%)"
              strokeWidth="12"
            />
            <circle
              cx="64"
              cy="64"
              r="56"
              fill="none"
              stroke="hsl(128 20% 36%)"
              strokeWidth="12"
              strokeDasharray={`${(score / 100) * 351.86} 351.86`}
              strokeLinecap="round"
              className="transition-all duration-1000"
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className={`text-3xl font-bold ${getScoreColor(score)}`}>{Math.round(score)}</span>
            <span className="text-xs text-muted-foreground">{getScoreLabel(score)}</span>
          </div>
        </div>
      </div>

      {/* Score Breakdown */}
      {breakdown && (
        <div className="space-y-4">
          <ScoreBar 
            icon={<CheckCircle weight="duotone" className="w-5 h-5" />}
            label="Authenticity"
            value={breakdown.authenticity_score}
            color="#2C3E2D"
          />
          <ScoreBar 
            icon={<Leaf weight="duotone" className="w-5 h-5" />}
            label="Sustainability"
            value={breakdown.sustainability_score}
            color="#4A6C4C"
          />
          <ScoreBar 
            icon={<Users weight="duotone" className="w-5 h-5" />}
            label="Social Impact"
            value={breakdown.social_impact_score}
            color="#C26D53"
          />
        </div>
      )}
    </div>
  );
};

const ScoreBar = ({ icon, label, value, color }) => (
  <div className="flex items-center gap-3">
    <div className="flex-shrink-0" style={{ color }}>{icon}</div>
    <div className="flex-1">
      <div className="flex justify-between text-sm mb-1">
        <span className="text-foreground/80">{label}</span>
        <span className="font-medium" style={{ color }}>{Math.round(value)}</span>
      </div>
      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <div 
          className="h-full rounded-full transition-all duration-1000"
          style={{ width: `${value}%`, backgroundColor: color }}
        />
      </div>
    </div>
  </div>
);

export default TransparencyCard;
